import { Component, DestroyRef, inject, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from "@angular/router";
import {
  NbDialogService,
  NbMenuItem,
  NbMenuService,
  NbPopoverDirective,
  NbSidebarService,
  NbButtonModule,
  NbActionsModule,
  NbTooltipModule,
  NbContextMenuModule,
  NbToastrService,
} from "@nebular/theme";
import { combineLatest, Observable, Subscription } from "rxjs";

import { TranslateService, TranslateModule } from "@ngx-translate/core";
import { filter, first, map, switchMap } from "rxjs/operators";
import { KPI } from "../../../@core/api";
import {
  ConfigService,
  ExportsService,
  LayoutService,
  PerimetersService,
  ReportsService,
  ScopeService,
  UsersService,
  UtilsService,
  YearsService
} from "../../../@core/utils";
import { TooMuchSitesDialogComponent } from "../too-much-sites-dialog/too-much-sites-dialog.component";
import { PowerpointReportingDialogComponent } from "../powerpoint-reporting-dialog/powerpoint-reporting-dialog.component";
import { ScenarioManagementDialogComponent } from "../scenario-management-dialog/scenario-management-dialog.component";
import { LanguageSelectionComponent } from "../language-selection/language-selection.component";
import { FontAwesomeModule } from "@fortawesome/angular-fontawesome";

import { PerimetersFiltersButtonComponent } from "../perimeters-filters-button/perimeters-filters-button.component";
import { YearRangeInputComponent } from "../year-range-input/year-range-input.component";
import { SiteOverviewComponent } from "../site-overview/site-overview.component";
import { PerimetersActiveFiltersComponent } from "../perimeters-active-filters/perimeters-active-filters.component";
import { CommonModule } from "@angular/common";
import { environment } from '../../../../environments/environment';
import { GROUP_PERMISSION_ACCESS_AUDIT } from "../../../@core/api/models";
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: "app-header",
  styleUrls: ["./header.component.scss"],
  templateUrl: "./header.component.html",
  standalone: true,
  imports: [
    YearRangeInputComponent,
    PerimetersFiltersButtonComponent,
    NbButtonModule,
    NbActionsModule,
    NbTooltipModule,
    FontAwesomeModule,
    LanguageSelectionComponent,
    NbContextMenuModule,
    TranslateModule,
    PerimetersActiveFiltersComponent,
    CommonModule
  ]
})
export class HeaderComponent implements OnInit, OnDestroy {
  @Input() public position = "normal";
  @ViewChild(NbPopoverDirective, { static: false })

  private destroyRef = inject(DestroyRef);

  public user: KPI.User;
  public userMenu: NbMenuItem[] = [
    {
      title: this.translate.instant("Log out"),
      link: "/auth/logout"
    }
  ];
  public reportLoading: boolean = false;
  public reportsMenu: NbMenuItem[] = [];
  public investmentExportReady: boolean = false;
  public assetsExportReady: boolean = false;
  public exportInvestsPending: boolean = false;
  public exportAssetsPending: boolean = false;
  private subscriptions: Subscription[] = [];
  public sitesLimit = 1000;
  public perimeters: KPI.Perimeter[];
  public exportInProgress: boolean = false;
  public canGoToAudit: boolean;

  showRisksExport$: Observable<boolean> = this.customerConfigService.customerConfig$.pipe(
    filter(config => !!config),
    map(config => config.risk_module_enabled)
  );

  showScenariiManager$: Observable<boolean> = this.usersService.hasScenarioManagementEnabled$;
  showEquipmentAndReserveExport$: Observable<boolean> = this.customerConfigService.customerConfig$.pipe(
    filter(config => !!config),
    map(config => config.equipment_and_reserve_xls_export)
  );
  showTaxonomyXLSReporting$: Observable<boolean> = this.customerConfigService.customerConfig$.pipe(
    filter(config => !!config),
    map(config => !!config.taxonomy_XLS_reporting)
  );
  showESRSXLSReporting$: Observable<boolean> = this.customerConfigService.customerConfig$.pipe(
    filter(config => !!config),
    map(config => !!config.ESRS_XLS_reporting)
  );
  public exportMenu$: Observable<NbMenuItem[]> = combineLatest([
    this.showRisksExport$,
    this.showEquipmentAndReserveExport$,
    this.showTaxonomyXLSReporting$,
    this.showESRSXLSReporting$,
  ]).pipe(
    map(([showRisks, showEquipmentExport, showTaxonomy, showESRS]) => {
      const menu: NbMenuItem[]= [];

      if (showEquipmentExport) {
        menu.push({
          title: this.translate.instant('theme.header.export_csv_equipments_observations'),
          data: { type: 'equipments_observations' }
        });
      }

      const exportPurchaseFlow = this.customerConfigService.getSettingValue('SYSTEM__EXPORT_RECONCILIATION_ARIBA', false)
      if (exportPurchaseFlow) {
        menu.push({
          title: this.translate.instant('theme.header.export_purchase_flow'),
          data: { type: 'purchase_flow' }
        });
      }

      menu.push({
        title: this.translate.instant('theme.header.export_ppat_site_by_site'),
        data: { type: 'multi_site' }
      });

      if (showRisks) {
        menu.push({
          title: this.translate.instant('theme.header.export_risks'),
          data: { type: 'risks' }
        });
      }

      if (showTaxonomy) {
        menu.push({
          title: this.translate.instant('theme.header.export_xls_taxonomy_capex'),
          data: { type: 'taxonomy_xls_reporting' }
        });
      }

      if (showESRS) {
        menu.push({
          title: this.translate.instant('theme.header.export_xls_esrs_transition'),
          data: { type: 'esrs_xls_reporting' }
        });
      }

      return menu;
    })
  );

  constructor(
    public router: Router,
    private exportsService: ExportsService,
    private nbDialogService: NbDialogService,
    private scopeService: ScopeService,
    private sidebarService: NbSidebarService,
    private perimetersService: PerimetersService,
    private translate: TranslateService,
    private reportsService: ReportsService,
    private nbMenuService: NbMenuService,
    private yearsService: YearsService,
    private customerConfigService: ConfigService,
    private utils: UtilsService,
    private layoutService: LayoutService,
    private usersService: UsersService,
    private toastr: NbToastrService,
  ) {}

  public ngOnInit(): void {
    this.subscriptions = [
      combineLatest([
        this.scopeService.user$,
        this.perimetersService.perimeters$,
        this.reportsService.isAnyReportDownloading$,
        this.reportsService.userReportTemplates$,
        this.usersService.hasCurrentUserPermissions([GROUP_PERMISSION_ACCESS_AUDIT]),
        this.customerConfigService.customerConfig$
      ]).subscribe(([user, perimeters, isAnyReportDownloading, reportTemplates, accessAllowed, config]) => {
        this.canGoToAudit = accessAllowed && config.can_access_audit_app
        this.reportLoading = isAnyReportDownloading;
        this.user = user;
        this.perimeters = perimeters;
        this.reportsMenu = [];
        reportTemplates.forEach(template => {
          if (
            (template.report_type_ref === "multi_site" && perimeters?.length > 1) ||
            (template.report_type_ref === "mono_site" && perimeters?.length === 1)
          ) {
            this.reportsMenu.push({
              title: template.label,
              data: template
            });
          }
        });
      }),
      this.nbMenuService.onItemClick().subscribe(item => {
        if (item && item.tag === 'exportMenu') {
          if (item.item.data?.type === 'risks') {
            this.exportRisks();
          } else if (item.item.data?.type === 'multi_site') {
            this.exportMultiSite();
          } else if (item.item.data?.type === 'equipments_observations') {
            this.exportEquipmentsObservations();
          } else if (item.item.data?.type === 'ppat_site_by_site') {
            this.exportPPATSiteBySite();
          } else if (item.item.data?.type === 'purchase_flow') {
            this.exportPurchaseFlow()
          } else if (item.item.data?.type === 'taxonomy_xls_reporting') {
            this.toastr.info(this.translate.instant('theme.header.export_xls_taxonomy_capex'), undefined, { duration: 3000 });
          } else if (item.item.data?.type === 'esrs_xls_reporting') {
            this.toastr.info(this.translate.instant('theme.header.export_xls_esrs_transition'), undefined, { duration: 3000 });
          }
        }
      })
    ];

    this.subscriptions.push(
      this.exportsService.investmentExportReady.subscribe((ready: boolean) => {
        this.investmentExportReady = ready;
        if (ready) {
          this.subscriptions.push(
            this.exportsService.investmentExportLoading.subscribe((loading: boolean) => {
              this.exportInvestsPending = loading;
            })
          );
        }
      }),
      this.exportsService.assetExportReady.subscribe((ready: boolean) => {
        this.assetsExportReady = ready;
        if (ready) {
          this.subscriptions.push(
            this.exportsService.assetExportLoading.subscribe((loading: boolean) => {
              this.exportAssetsPending = loading;
            })
          );
        }
      })
    );

    // Handle report menu clicks
    combineLatest([
      this.nbMenuService.onItemClick().pipe(filter(item => item.tag === 'reportsMenu')),
      this.yearsService.latestYears$.pipe(first())
    ]).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(([item, [startYear, endYear]]) => {
      if (!item.item.data) {
        return;
      }

      const template = item.item.data;
      const templateId = template.id;
      const type_ref = template.report_type_ref;

      // front report
      if (templateId == "powerpoint-reporting") {
        this.nbDialogService.open(PowerpointReportingDialogComponent, {
          closeOnBackdropClick: false
        });
      }
      // back reports
      else {
        const inputData: KPI.ReportTemplateInputData = {
          start_year: startYear,
          end_year: endYear
        };
        const selectedMultiPerimeters = this.perimeters.filter(p => !p.is_monosite).map(p => p.id);
        if (type_ref === "multi_site") {
          inputData.perimeters_id = selectedMultiPerimeters;
        } else if (type_ref === "mono_site") {
          inputData.perimeter_id = selectedMultiPerimeters[0];
        }

        inputData.buildings_id = PerimetersService.getBuildingsIds(
          this.perimeters.filter(p => !p.is_monosite)
        );

        this.nbDialogService.open(PowerpointReportingDialogComponent, {
          closeOnBackdropClick: false,
          context: {
            inputData,
            template
          }
        });
      }
    });

  }

  public ngOnDestroy(): void {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }

  public toggleSidebar(): boolean {
    this.sidebarService.toggle(false, "menu-sidebar");

    return false;
  }

  public tooMuchSitesSelected(): void {
    if (this.perimeters && this.perimeters.length > this.sitesLimit) {
      this.nbDialogService.open(TooMuchSitesDialogComponent, {
        autoFocus: false,
        context: {
          label: this.translate.instant("theme.header.reports.reporting"),
          sitesLimit: this.sitesLimit
        }
      });
    }
  }

  public seeSiteOverview(): void {
    const perimeter = this.perimeters[0];
    const dialog = this.nbDialogService.open(
      SiteOverviewComponent,
      {
        autoFocus: false,
        closeOnBackdropClick: false,
        context: { perimeter }
      }
    );
    dialog.onClose.subscribe(() => {
      if (this.layoutService.sheetDialogNb !== 0) {
        this.layoutService.resetSheetDialogNb();
      }
    });
  }

  public openScenarii(): void {
    this.nbDialogService.open(ScenarioManagementDialogComponent, {
      autoFocus: false
    });
  }

  exportRisks() {
    this.exportInProgress = true;
    this.perimetersService.perimeters$
      .pipe(
        first(),
        map(perimeters => perimeters.map(p => p.id)),
        switchMap(pids => this.exportsService.downloadRisksExport({ site_ids: pids }))
      )
      .subscribe({
        next: () => {
          this.exportInProgress = false;
        },
        error: () => {
          this.exportInProgress = false;
        }
      });
  }

  exportMultiSite() {
    this.exportInProgress = true;
    const savedFilters = this.perimetersService.perimeterFilterSelection;
    const clusterFilter = savedFilters.find((f: any) => f.id === 'cluster');
    const typeFilter = savedFilters.find((f: any) => f.id === 'parent-perimeter-type');
    const cluster_ids = clusterFilter ? (clusterFilter.selectedValues || []).map(Number) : [];
    const perimeter_type_ids = typeFilter ? (typeFilter.selectedValues || []).map(Number) : [];

    combineLatest([
      this.perimetersService.buildingIds$,
      this.yearsService.latestYears$
    ]).pipe(
      first(),
      switchMap(([buildings, [startYear, endYear]]) =>
        this.exportsService.downloadMultiSiteExport({
          buildings,
          start_year: startYear,
          end_year: endYear,
          ...(cluster_ids.length ? { cluster_ids } : {}),
          ...(perimeter_type_ids.length ? { perimeter_type_ids } : {}),
        })
      ),
    ).subscribe({
        next: () => {
          this.exportInProgress = false;
        },
        error: () => {
          this.exportInProgress = false;
        }
      });
  }

  exportEquipmentsObservations() {
    // Will be implemented via API endpoint
    // this.exportsService.downloadEquipmentsObservationsExport({ buildings }).subscribe(...)
  }

   private exportPurchaseFlow() {
     this.exportInProgress = true;
     this.perimetersService.buildingIds$
       .pipe(
         first(),
         switchMap((buildings) =>
           this.exportsService.downloadPurchaseFlowExport({ buildings })
         ),
       )
       .subscribe({
         next: () => {
           this.exportInProgress = false;
         },
         error: () => {
           this.exportInProgress = false;
         }
       });
   }

  exportPPATSiteBySite() {
    // Will be implemented via API endpoint
    // this.exportsService.downloadPPATSiteBySiteExport({ buildings }).subscribe(...)
  }

  // show the scenarii management when
  // - energy module (admin/superadmin only)
  // - investment module + backend flag (admin/superadmin only)
  public get showScenariiMenu$() {
    if (this.router.url.includes("/energy/") || this.router.url.includes("/decision/spreadsheet")) {
      return this.showScenariiManager$;
    }
    return this.showScenariiManager$.pipe(
      map((enabled) => enabled && this.router.url.includes("decision"))
    )
  }

  public get hidePeriodAndScope(): boolean {
    return this.router.url.includes("/administration");
  }

  public goToAudit() {
    const popupUrl = `${environment.auditAppURL}/perimeters`
    this.utils.openPopupWindow(popupUrl)
  }
}
